import { useEffect, useMemo, useRef, useState } from "react";

import { useLoaderContext } from '../../../../components/Loader/LoaderContext';

import DashboardHeader from '../../components/DashboardHeader';

import ProductivityHeader from '../../components/ManagerDashboard/ProductivityReport/ProductivityHeader';
import ProductivityKPICards from '../../components/ManagerDashboard/ProductivityReport/ProductivityKpiCards';
import TeamProductivityChart from '../../components/ManagerDashboard/ProductivityReport/ProductivityByTeamChart';
import ProductivityTrendChart from '../../components/ManagerDashboard/ProductivityReport/WtdTrendChart';
import ProductiveAppMix from '../../components/ManagerDashboard/ProductivityReport/ProductiveAppMixChart';
import ProductivityDistribution from '../../components/ManagerDashboard/ProductivityReport/ProductivityDistributionChart';
import ProductivityAssociatesTable from '../../components/ManagerDashboard/ProductivityReport/AssociateProductivityTable';

import useProductivityReport from '../../hooks/Manager/ProductivityReport/useProductivityReport';

import {
  getManagerEmployeeCode,
} from '../../../../features/auth/utils/Login/authStorage';

import ProductivityFilters from '../../components/ManagerDashboard/ProductivityReport/ProductivityFilters';

import { ManagerDateProvider } from '../../context/ManagerDateContext';


// ============================================================================
// BROWSER ZOOM AUTO REFRESH
// ============================================================================
//
// Chrome does not provide a direct "page zoom changed" event.
//
// This implementation intentionally combines:
//   1. devicePixelRatio
//   2. window.outerWidth / window.innerWidth ratio
//   3. CSS resolution media query
//   4. resize events
//   5. a short polling interval
//
// The outerWidth / innerWidth ratio is useful when devicePixelRatio does not
// change in a particular Chrome/Windows configuration. A normal browser
// window resize generally changes both outerWidth and innerWidth together,
// so their ratio remains approximately stable.
//
// When a real zoom change is detected, the page reloads once after Chrome
// finishes applying the new zoom level.
//
// ============================================================================

const useBrowserZoomAutoRefresh = () => {
  const initialDprRef = useRef<number>(
    Number(window.devicePixelRatio.toFixed(3)),
  );

  const initialZoomRatioRef = useRef<number>(
    window.innerWidth > 0
      ? window.outerWidth / window.innerWidth
      : 1,
  );

  const reloadStartedRef =
    useRef<boolean>(false);

  const resizeTimerRef =
    useRef<number | null>(null);

  const pollTimerRef =
    useRef<number | null>(null);

  const resolutionQueryRef =
    useRef<MediaQueryList | null>(null);

  const resolutionHandlerRef =
    useRef<(() => void) | null>(null);

  useEffect(() => {
    // ------------------------------------------------------------------------
    // Capture the values AFTER the page has mounted.
    // ------------------------------------------------------------------------

    initialDprRef.current =
      Number(
        window.devicePixelRatio.toFixed(3),
      );

    initialZoomRatioRef.current =
      window.innerWidth > 0
        ? window.outerWidth /
          window.innerWidth
        : 1;

    reloadStartedRef.current = false;

    // ------------------------------------------------------------------------
    // Reload
    // ------------------------------------------------------------------------

    const reloadPage = (
      reason: string,
    ) => {
      if (reloadStartedRef.current) {
        return;
      }

      reloadStartedRef.current = true;

      console.log(
        '[Browser Zoom] Zoom change detected:',
        reason,
        {
          devicePixelRatio:
            window.devicePixelRatio,
          innerWidth:
            window.innerWidth,
          outerWidth:
            window.outerWidth,
          innerHeight:
            window.innerHeight,
          outerHeight:
            window.outerHeight,
        },
      );

      // Let Chrome finish applying the zoom before reload.
      window.setTimeout(() => {
        window.location.reload();
      }, 200);
    };

    // ------------------------------------------------------------------------
    // Check whether browser page zoom changed.
    // ------------------------------------------------------------------------

    const checkZoom = () => {
      if (reloadStartedRef.current) {
        return;
      }

      const currentDpr =
        Number(
          window.devicePixelRatio.toFixed(3),
        );

      const currentZoomRatio =
        window.innerWidth > 0
          ? window.outerWidth /
            window.innerWidth
          : 1;

      // DPR is the strongest signal.
      const dprChanged =
        Math.abs(
          currentDpr -
            initialDprRef.current,
        ) >= 0.01;

      // Ratio catches Chrome configurations where DPR is unchanged.
      //
      // A small tolerance is used because browser chrome/frame dimensions
      // can introduce tiny floating-point differences.
      const ratioChanged =
        Math.abs(
          currentZoomRatio -
            initialZoomRatioRef.current,
        ) >= 0.03;

      if (dprChanged) {
        reloadPage(
          'devicePixelRatio changed',
        );
        return;
      }

      if (ratioChanged) {
        reloadPage(
          'outerWidth/innerWidth zoom ratio changed',
        );
      }
    };

    // ------------------------------------------------------------------------
    // Resize handler.
    // ------------------------------------------------------------------------

    const handleResize = () => {
      if (
        resizeTimerRef.current !== null
      ) {
        window.clearTimeout(
          resizeTimerRef.current,
        );
      }

      resizeTimerRef.current =
        window.setTimeout(() => {
          checkZoom();
        }, 150);
    };

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );

    // ------------------------------------------------------------------------
    // Visual viewport.
    // ------------------------------------------------------------------------

    const visualViewport =
      window.visualViewport;

    const handleVisualViewportResize =
      () => {
        // Delay slightly so Chrome has time to update all viewport values.
        window.setTimeout(() => {
          checkZoom();
        }, 50);
      };

    if (visualViewport) {
      visualViewport.addEventListener(
        'resize',
        handleVisualViewportResize,
      );
    }

    // ------------------------------------------------------------------------
    // CSS resolution media query.
    // ------------------------------------------------------------------------

    const setupResolutionQuery = () => {
      const currentDpr =
        Number(
          window.devicePixelRatio.toFixed(3),
        );

      const query =
        window.matchMedia(
          `(resolution: ${currentDpr}dppx)`,
        );

      const handleResolutionChange =
        () => {
          // The media query can fire while Chrome is still changing zoom.
          window.setTimeout(() => {
            checkZoom();
          }, 50);
        };

      resolutionQueryRef.current =
        query;

      resolutionHandlerRef.current =
        handleResolutionChange;

      if (
        typeof query.addEventListener ===
        'function'
      ) {
        query.addEventListener(
          'change',
          handleResolutionChange,
        );
      } else {
        query.addListener(
          handleResolutionChange,
        );
      }
    };

    setupResolutionQuery();

    // ------------------------------------------------------------------------
    // Polling fallback.
    //
    // This is intentionally frequent because the user wants the page to
    // refresh automatically immediately after increasing Chrome zoom.
    // ------------------------------------------------------------------------

    pollTimerRef.current =
      window.setInterval(() => {
        checkZoom();
      }, 100);

    // Initial check.
    checkZoom();

    // ------------------------------------------------------------------------
    // Cleanup.
    // ------------------------------------------------------------------------

    return () => {
      window.removeEventListener(
        'resize',
        handleResize,
      );

      if (visualViewport) {
        visualViewport.removeEventListener(
          'resize',
          handleVisualViewportResize,
        );
      }

      if (
        resizeTimerRef.current !== null
      ) {
        window.clearTimeout(
          resizeTimerRef.current,
        );

        resizeTimerRef.current = null;
      }

      if (
        pollTimerRef.current !== null
      ) {
        window.clearInterval(
          pollTimerRef.current,
        );

        pollTimerRef.current = null;
      }

      const query =
        resolutionQueryRef.current;

      const handler =
        resolutionHandlerRef.current;

      if (query && handler) {
        if (
          typeof query.removeEventListener ===
          'function'
        ) {
          query.removeEventListener(
            'change',
            handler,
          );
        } else {
          query.removeListener(handler);
        }
      }

      resolutionQueryRef.current =
        null;

      resolutionHandlerRef.current =
        null;
    };
  }, []);
};


// ============================================================================
// PRODUCTIVITY REPORT CONTENT
// ============================================================================

const ProductivityReportContent = () => {

  // ==========================================================================
  // BROWSER ZOOM AUTO REFRESH
  // ==========================================================================

  useBrowserZoomAutoRefresh();


  // ==========================================================================
  // MANAGER EMPLOYEE CODE
  // ==========================================================================

  const managerEmployeeCode =
    getManagerEmployeeCode();


  // ==========================================================================
  // LOADER
  // ==========================================================================

  const {
    hideLoader,
  } = useLoaderContext();


  // ==========================================================================
  // PRODUCTIVITY REPORT
  // ==========================================================================

  const {
    loading,

    // KPI
    kpiCards,
    kpiError,

    // Charts
    teamProductivity,
    productivityTrend,
    associates,
    appMix,
    distribution,

    // Errors
    teamError,
    trendError,
    associateError,
    appMixError,
    distributionError,

  } = useProductivityReport({
    managerEmployeeCode,
  });


  // ==========================================================================
  // LOADER
  // ==========================================================================

  useEffect(() => {

    if (!loading) {

      const timer =
        setTimeout(() => {

          hideLoader();

        }, 500);


      return () => {

        clearTimeout(timer);

      };

    }


    return undefined;

  }, [
    loading,
    hideLoader,
  ]);


  // ==========================================================================
  // FILTER STATE
  // ==========================================================================

  const [
    selectedTeam,
    setSelectedTeam,
  ] = useState('All');


  const [
    selectedLocation,
    setSelectedLocation,
  ] = useState('All');


  const [
    selectedShift,
    setSelectedShift,
  ] = useState('All');


  const [
    search,
    setSearch,
  ] = useState('');


  // ==========================================================================
  // TEAM OPTIONS
  // ==========================================================================

  const teamOptions =
    useMemo(() => {

      const uniqueTeams = [
        ...new Set(
          associates
            .map(
              (item) =>
                item.teamLeadName,
            )
            .filter(Boolean),
        ),
      ].sort();


      return [
        'All',
        ...uniqueTeams,
      ];

    }, [
      associates,
    ]);


  // ==========================================================================
  // LOCATION OPTIONS
  // ==========================================================================

  const locationOptions =
    useMemo(() => {

      const uniqueLocations = [
        ...new Set(
          associates
            .map(
              (item) =>
                item.city,
            )
            .filter(Boolean),
        ),
      ].sort();


      return [
        'All',
        ...uniqueLocations,
      ];

    }, [
      associates,
    ]);


  // ==========================================================================
  // SHIFT OPTIONS
  // ==========================================================================

  const shiftOptions =
    useMemo(() => {

      const uniqueShifts = [
        ...new Set(
          associates
            .map(
              (item) =>
                item.shiftName,
            )
            .filter(Boolean),
        ),
      ].sort();


      return [
        'All',
        ...uniqueShifts,
      ];

    }, [
      associates,
    ]);


  // ==========================================================================
  // FILTER ASSOCIATES
  // ==========================================================================

  const filteredAssociates =
    useMemo(() => {

      const normalizedSearch =
        search
          .trim()
          .toLowerCase();


      return associates.filter(
        (item) => {

          const matchesTeam =
            selectedTeam === 'All' ||
            item.teamLeadName ===
              selectedTeam;


          const matchesLocation =
            selectedLocation === 'All' ||
            item.city ===
              selectedLocation;


          const matchesShift =
            selectedShift === 'All' ||
            item.shiftName ===
              selectedShift;


          const matchesSearch =
            !normalizedSearch ||
            item.employeeName
              ?.toLowerCase()
              .includes(
                normalizedSearch,
              ) ||
            item.employeeCode
              ?.toLowerCase()
              .includes(
                normalizedSearch,
              ) ||
            item.teamLeadName
              ?.toLowerCase()
              .includes(
                normalizedSearch,
              );


          return (
            matchesTeam &&
            matchesLocation &&
            matchesShift &&
            matchesSearch
          );

        },
      );

    }, [
      associates,
      selectedTeam,
      selectedLocation,
      selectedShift,
      search,
    ]);


  // ==========================================================================
  // FILTER TEAM PRODUCTIVITY CHART
  // ==========================================================================

  const filteredTeamProductivity =
    useMemo(() => {

      if (
        selectedTeam ===
        'All'
      ) {

        return teamProductivity;

      }


      return teamProductivity.filter(
        (item) =>
          item.teamLeadName ===
          selectedTeam,
      );

    }, [
      teamProductivity,
      selectedTeam,
    ]);


  // ==========================================================================
  // MANAGER CODE VALIDATION
  // ==========================================================================

  if (!managerEmployeeCode) {

    return (
      <div
        className="
          min-h-screen
          bg-[#F5F7FB]
        "
      >

        <DashboardHeader
          showBackButton
          breadcrumbs={[
            {
              label:
                'Work Vista',
              path: '/',
            },
            {
              label:
                'Reporting Manager Dashboard',
              path: '/rm-dashboard',
            },
            {
              label:
                'Productivity Report',
            },
          ]}
        />


        <div
          className="
            flex
            h-[70vh]
            items-center
            justify-center
            text-red-600
          "
        >
          Manager Employee Code not found in Local Storage.
        </div>

      </div>
    );

  }


  // ==========================================================================
  // PAGE
  // ==========================================================================

  return (
    <div
      className="
        min-h-screen
        space-y-5
        bg-[#F5F7FB]
      "
    >

      {/* ======================================================================
          DASHBOARD HEADER
          ====================================================================== */}

      <DashboardHeader
        showBackButton
        breadcrumbs={[
          {
            label:
              'Work Vista',
            path: '/',
          },
          {
            label:
              'Reporting Manager Dashboard',
            path: '/rm-dashboard',
          },
          {
            label:
              'Productivity Report',
          },
        ]}
      />


      {/* ======================================================================
          CONTENT AREA
          ====================================================================== */}

      <div
        className="
          relative
          min-h-[500px]
          space-y-6
          overflow-hidden
          rounded-2xl
          border
          border-slate-200
          bg-white
          p-5
        "
      >

        {/* ====================================================================
            LOCAL LOADER
            ==================================================================== */}

        {loading && (

          <div
            className="
              absolute
              inset-0
              z-[9999]
              flex
              items-center
              justify-center
              bg-white/80
              backdrop-blur-[1px]
            "
          >

            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                rounded-xl
                bg-white
                px-8
                py-6
                shadow-lg
              "
            >

              {/* Spinner */}

              <div
                className="
                  h-10
                  w-10
                  animate-spin
                  rounded-full
                  border-[3px]
                  border-[#E5E7EB]
                  border-t-[#2563EB]
                "
              />


              {/* Text */}

              <p
                className="
                  mt-3
                  text-[12px]
                  font-semibold
                  text-[#344054]
                "
              >
                Loading data...
              </p>


              <p
                className="
                  mt-1
                  text-[10px]
                  text-[#98A2B3]
                "
              >
                Please wait
              </p>

            </div>

          </div>

        )}


        {/* ====================================================================
            PRODUCTIVITY HEADER
            ==================================================================== */}

        <ProductivityHeader
          averageProductivity={
            kpiCards?.currentAvgProductivity ??
            0
          }
          totalTeamLeads={
            kpiCards?.totalTeamLeads ??
            0
          }
          totalAssociates={
            kpiCards?.totalAssociates ??
            0
          }
        />


        {/* ====================================================================
            KPI CARDS
            ==================================================================== */}

        {kpiError ? (

          <div
            className="
              rounded-xl
              border
              border-red-200
              bg-red-50
              p-6
              text-center
              text-red-600
            "
          >
            {kpiError}
          </div>

        ) : (

          <ProductivityKPICards
            data={
              kpiCards
            }
          />

        )}


        {/* ====================================================================
            FILTERS
            ==================================================================== */}

        <ProductivityFilters
          teams={
            teamOptions
          }
          locations={
            locationOptions
          }
          shifts={
            shiftOptions
          }

          selectedTeam={
            selectedTeam
          }

          selectedLocation={
            selectedLocation
          }

          selectedShift={
            selectedShift
          }

          search={
            search
          }

          onTeamChange={
            setSelectedTeam
          }

          onLocationChange={
            setSelectedLocation
          }

          onShiftChange={
            setSelectedShift
          }

          onSearchChange={
            setSearch
          }
        />


        {/* ====================================================================
            TEAM PRODUCTIVITY + WTD TREND
            ==================================================================== */}

        <div
          className="
            grid
            grid-cols-1
            gap-5
            xl:grid-cols-2
          "
        >

          {/* Team Productivity */}

          {teamError ? (

            <div
              className="
                flex
                h-[330px]
                items-center
                justify-center
                rounded-xl
                border
                border-red-200
                bg-red-50
                text-red-600
              "
            >
              {teamError}
            </div>

          ) : (

            <TeamProductivityChart
              data={
                filteredTeamProductivity
              }
            />

          )}


          {/* Productivity Trend */}

          {trendError ? (

            <div
              className="
                flex
                h-[330px]
                items-center
                justify-center
                rounded-xl
                border
                border-red-200
                bg-red-50
                text-red-600
              "
            >
              {trendError}
            </div>

          ) : (

            <ProductivityTrendChart
              data={
                productivityTrend
              }
            />

          )}

        </div>


        {/* ====================================================================
            ASSOCIATES
            ==================================================================== */}

        {associateError ? (

          <div
            className="
              rounded-xl
              border
              border-red-200
              bg-red-50
              p-8
              text-center
              text-red-600
            "
          >
            {associateError}
          </div>

        ) : (

          <ProductivityAssociatesTable
            data={
              filteredAssociates
            }
          />

        )}


        {/* ====================================================================
            APP MIX + DISTRIBUTION
            ==================================================================== */}

        <div
          className="
            grid
            grid-cols-1
            gap-6
            lg:grid-cols-2
          "
        >

          {/* Productive App Mix */}

          {appMixError ? (

            <div
              className="
                rounded-xl
                border
                border-red-200
                bg-red-50
                p-8
                text-center
                text-red-600
              "
            >
              {appMixError}
            </div>

          ) : (

            <ProductiveAppMix
              data={
                appMix
              }
            />

          )}


          {/* Productivity Distribution */}

          {distributionError ? (

            <div
              className="
                rounded-xl
                border
                border-red-200
                bg-red-50
                p-8
                text-center
                text-red-600
              "
            >
              {distributionError}
            </div>

          ) : (

            <ProductivityDistribution
              data={
                distribution
              }
            />

          )}

        </div>

      </div>

    </div>
  );
};


// ============================================================================
// PAGE
// ============================================================================

const ProductivityReport = () => {

  return (
    <ManagerDateProvider>
      <ProductivityReportContent />
    </ManagerDateProvider>
  );

};


export default ProductivityReport;